import { useMutation, useQueryClient } from '@tanstack/react-query'
import { OrgErrorCode } from '@today/types'
import type { OrgMoveOptionView } from '@today/ui-domain'
import { useMemo, useState } from 'react'
import { type OrgUnitDTO, organizationService } from '@/services/organization.service'
import type { OrgT } from './org-editor'
import { errorMessageKey, toMutationError } from './org-errors'
import { isLegalMoveTarget, moveTargetIds } from './org-move'
import { normalizeSearch, type OrgTreeModel } from './org-tree'
import { ORG_UNITS_KEY } from './useOrgTree'
import { orgUnitKey } from './useOrgUnit'

/** The picker renders at most this many matching parents. */
const MAX_OPTIONS = 100

const pickerOptions = (model: OrgTreeModel<OrgUnitDTO>, unitId: string, query: string): OrgMoveOptionView[] => {
	const needle = normalizeSearch(query)
	const options: OrgMoveOptionView[] = []
	for (const id of moveTargetIds(model, unitId)) {
		const unit = model.byId.get(id) as OrgUnitDTO
		const key = model.searchKey.get(id) ?? ''
		if (needle === '' || key.split('\u0000').some((part) => part.includes(needle))) {
			options.push({ id, code: unit.code, name: unit.name })
		}
		if (options.length >= MAX_OPTIONS) break
	}
	return options
}

/**
 * Reparenting (REQ-006): the Move to… dialog and drag-and-drop share one
 * mutation and the client-side self/descendant check. A refusal leaves the tree
 * as it was (nothing refetches); success refetches the tree and the unit.
 */
export function useOrgMove({ model, t }: { model: OrgTreeModel<OrgUnitDTO>; t: OrgT }) {
	const client = useQueryClient()
	const [dialog, setDialog] = useState<{
		unit: OrgUnitDTO
		query: string
		target: string | null | undefined
		error: string | null
	} | null>(null)
	const [dropError, setDropError] = useState<string | null>(null)
	const move = useMutation({
		mutationFn: ({ unitId, targetId }: { unitId: string; targetId: string | null }) =>
			organizationService.moveUnit(unitId, targetId === null ? null : (model.byId.get(targetId)?.code ?? null)),
		onSuccess: async (_saved, { unitId }) => {
			setDialog(null)
			setDropError(null)
			await Promise.all([
				client.invalidateQueries({ queryKey: ORG_UNITS_KEY }),
				client.invalidateQueries({ queryKey: orgUnitKey(unitId) }),
			])
		},
	})
	const options = useMemo(() => (dialog ? pickerOptions(model, dialog.unit.id, dialog.query) : []), [dialog, model])
	return {
		dialog,
		options,
		dropError,
		isMoving: move.isPending,
		open: (unit: OrgUnitDTO) => setDialog({ unit, query: '', target: undefined, error: null }),
		close: () => setDialog(null),
		setQuery: (query: string) => setDialog((d) => (d ? { ...d, query } : d)),
		setTarget: (target: string | null) => setDialog((d) => (d ? { ...d, target } : d)),
		confirm: () => {
			if (!dialog || dialog.target === undefined) return
			move.mutate(
				{ unitId: dialog.unit.id, targetId: dialog.target },
				{ onError: (err) => setDialog((d) => (d ? { ...d, error: toMutationError(err, t).message } : d)) },
			)
		},
		isLegalDrop: (unitId: string, targetId: string | null) => isLegalMoveTarget(model, unitId, targetId),
		drop: (unitId: string, targetId: string | null) => {
			if (!isLegalMoveTarget(model, unitId, targetId)) {
				setDropError(t(errorMessageKey(OrgErrorCode.HIERARCHY_CYCLE)))
				return
			}
			move.mutate({ unitId, targetId }, { onError: (err) => setDropError(toMutationError(err, t).message) })
		},
	}
}
